import { DEFAULT_LOGGER, Log } from "./logger";
import { METRICS } from "./telemetry/constants";
import { isESCPartition } from "./utils";

export const DEFAULT_GEOLOCATION_URL = "https://prod.tools.shortbread.aws.dev/1x1.png";
export const DEFAULT_CONSOLE_INTEGRATION_GEOLOCATION_URL = "https://prod.tools.shortbread.panorama.console.api.aws/ping";
export const FALLBACK_CONSOLE_INTEGRATION_GEOLOCATION_URL = "https://prod.tools.shortbread.analytics.console.aws.a2z.com/ping";
export const QUERY_PARAM_KEY = "awsccc";


export const timestampUrl = (url: string): string => {
    if (url.indexOf("?") !== -1) {
        const splittedUrl = url.split("?");
        // if url has params or if it has params AND hash values, insert our k-v pair immediately after the '?'
        url = splittedUrl[0] + `?${QUERY_PARAM_KEY}=${Date.now()}&` + splittedUrl[1]
    } else if (url.indexOf("#") !== -1) {
        const splittedUrl = url.split("#");
        // if url only has hash values, insert k-v pair before the '#'
        url = splittedUrl[0] + `?${QUERY_PARAM_KEY}=${Date.now()}#` + splittedUrl[1]
    } else {
        // if no params or hash values are found, append k-v pair to the end
        return (url + `?${QUERY_PARAM_KEY}=${Date.now()}`)
    }
    return url
}

export const queryGeolocationByHttpGetRequest =
    (hasConsoleNavFooter = false, url: string = DEFAULT_GEOLOCATION_URL, timeoutMillis = 5000, defaultLogger: Log = DEFAULT_LOGGER): GeolocationQuery => {
        function logLatency(log: Log, latency: number, region: string, message: string, status: number) {
            log("info")(METRICS.CHECK_GEOLOCATION_SUCCESS, {
                metric: latency,
                region,
                detail: message,
                url,
                status: status
            });
        }

        return (geolocatedIn: GeolocationQueryResultCallback, log: Log = defaultLogger || DEFAULT_LOGGER) => {
            // Always suppress the banner for domains that end with ".cn"
            if (window.location.hostname.endsWith('.cn')) {
                return geolocatedIn("NON-EU")
            }

            if (isESCPartition()) {
                return geolocatedIn("EU")
            }
            const geoServiceStart = Date.now();

            const makeRequest = (requestUrl: string, isFallback: boolean = false) => {
                const xhr = new XMLHttpRequest();
                let region: GeolocationRegion = "EU"; // default assumption
                let geoRespStatus = 200; // default assumption is 200 (in-EU)
                if (hasConsoleNavFooter) xhr.overrideMimeType("application/json");

                xhr.addEventListener("load", function (this: XMLHttpRequest) {
                    geoRespStatus = this.status;
                    if (!hasConsoleNavFooter || geoRespStatus === 304) {
                        // non-console integration or got 304
                        region = geoRespStatus === 403 ? "NON-EU" : "EU";
                    } else {
                        // handle response body returned from DEFAULT_CONSOLE_INTEGRATION_GEOLOCATION_URL
                        try {
                            let geoRespBody = JSON.parse(this.response);
                            geoRespStatus = geoRespBody.status;
                            if (geoRespStatus === 403) region = "NON-EU";
                        } catch (e) {
                            log("error")("geolocationResponseError", { url: requestUrl, detail: "Failed to Parse the Received Geolocation Response" });
                        }
                    }
                    logLatency(log, Date.now() - geoServiceStart, region, `Geolocation Response Received${isFallback ? ' (Fallback)' : ''}`, geoRespStatus);
                    geolocatedIn(region);
                });

                const timeoutHandler = () => {
                    if (!isFallback) { // First request's fallback is 'false' and timed out
                        // Try fallback domain
                        makeRequest(FALLBACK_CONSOLE_INTEGRATION_GEOLOCATION_URL, true);
                    } else {
                        // Both attempts failed, default to EU
                        const region = "EU";
                        const message = "Geolocation Request Failed (Both Domains)";
                        logLatency(log, timeoutMillis, region, message, 0);
                        log("error")("geolocationRequestError", {
                            url,
                            fallbackUrl: requestUrl,
                            timeoutSetting: timeoutMillis,
                            detail: message
                        });
                        geolocatedIn(region);
                    }
                };

                xhr.addEventListener("timeout", timeoutHandler);
                xhr.addEventListener("error", timeoutHandler);
                xhr.open("GET", timestampUrl(requestUrl));
                xhr.timeout = timeoutMillis;
                xhr.send();
            };
            makeRequest(url);
        };
    }

export type GeolocationRegion = "EU" | "NON-EU";
export type GeolocationQueryResultCallback = (region: GeolocationRegion) => void;
export type GeolocationQuery = (geolocatedIn: GeolocationQueryResultCallback, log?: Log) => void;
export default queryGeolocationByHttpGetRequest;
